import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { RefreshCw, Wrench, Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import './ConflictPanel.css';

function ConflictPanel({ conflicts, onConflictResolve, onRefreshConflicts, onAutoFixConflicts, isRefreshing = false }) {

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return '🚨';
      case 'moderate': return '⚠️';
      case 'warning': return '💡';
      default: return 'ℹ️';
    }
  };

  const getSeverityVariant = (severity) => {
    switch (severity) {
      case 'critical': return 'destructive';
      case 'moderate': return 'warning';
      case 'warning': return 'info';
      default: return 'secondary';
    }
  };

  const formatConflictType = (type) => {
    return type.replace(/_/g, ' ')
               .split(' ')
               .map(word => word.charAt(0).toUpperCase() + word.slice(1))
               .join(' ');
  };

  const renderConflictDetails = (conflict, index) => {
    switch (conflict.type) {
      case 'duplicate_player':
        return (
          <div className="conflict-details">
            <p><strong>Player:</strong> {conflict.player.name} ({conflict.player.ghin})</p>
            <p><strong>Groups:</strong> {conflict.groups.join(', ')}</p>
            <p className="conflict-suggestion">
              🔧 Remove player from one of the groups to resolve this conflict.
            </p>
          </div>
        );

      case 'unmatched_partner_request':
        return (
          <div className="conflict-details">
            <p><strong>Player:</strong> {conflict.player.name} ({conflict.player.ghin})</p>
            <p><strong>Requested Partner:</strong> {conflict.requestedPartner.name} {conflict.requestedPartner.ghin ? `(${conflict.requestedPartner.ghin})` : '(not found)'}</p>
            <p className="conflict-suggestion">
              {conflict.requestedPartner.ghin ? 
                `🔧 Move ${conflict.requestedPartner.name} to the same group as ${conflict.player.name}, or remove the partner request.` :
                `🔧 Partner "${conflict.requestedPartner.name}" was not found in the tournament. Verify the name or remove the request.`
              }
            </p>
          </div>
        );

      case 'time_preference_conflict':
        return (
          <div className="conflict-details">
            <p><strong>Group:</strong> {conflict.groupId || conflict.groupIndex}</p>
            <div className="player-lists">
              {conflict.earlyPlayers && conflict.earlyPlayers.length > 0 && (
                <div>
                  <strong>Early Players:</strong>
                  <ul>
                    {conflict.earlyPlayers.map(p => (
                      <li key={p.ghin}>{p.name} ({p.ghin})</li>
                    ))}
                  </ul>
                </div>
              )}
              {conflict.latePlayers && conflict.latePlayers.length > 0 && (
                <div>
                  <strong>Late Players:</strong>
                  <ul>
                    {conflict.latePlayers.map(p => (
                      <li key={p.ghin}>{p.name} ({p.ghin})</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <p className="conflict-suggestion">
              🔧 Consider moving players with different time preferences to separate groups.
            </p>
          </div>
        );

      case 'pace_violation':
        return (
          <div className="conflict-details">
            <p><strong>Group:</strong> {conflict.groupId || conflict.groupIndex}</p>
            <p><strong>Message:</strong> {conflict.message}</p>
            {conflict.players && conflict.players.length > 0 && (
              <div className="slow-players-list">
                <strong>Affected Players:</strong>
                <ul>
                  {conflict.players.map(p => (
                    <li key={p.ghin}>{p.name} ({p.ghin})</li>
                  ))}
                </ul>
              </div>
            )}
            {conflict.slowPlayers && conflict.slowPlayers.length > 0 && (
              <div className="slow-players-list">
                <strong>Slow Players:</strong>
                <ul>
                  {conflict.slowPlayers.map(p => (
                    <li key={p.ghin}>{p.name} ({p.ghin})</li>
                  ))}
                </ul>
              </div>
            )}
            <p className="conflict-suggestion">
              🔧 Move some slower players to other groups to improve pace balance.
            </p>
          </div>
        );

      case 'pace_overflow':
        return (
          <div className="conflict-details">
            <p><strong>Overflow Players:</strong> {conflict.players ? conflict.players.length : 0}</p>
            {conflict.players && conflict.players.length > 0 && (
              <div className="overflow-players-list">
                <ul>
                  {conflict.players.map(p => (
                    <li key={p.ghin}>{p.name} ({p.ghin})</li>
                  ))}
                </ul>
              </div>
            )}
            <p className="conflict-suggestion">
              🔧 These players need manual placement. Drag them to appropriate groups.
            </p>
          </div>
        );

      default:
        return (
          <div className="conflict-details">
            <p>{conflict.reason}</p>
          </div>
        );
    }
  };

  const groupConflictsBySeverity = () => {
    const grouped = {
      critical: [],
      moderate: [],
      warning: [],
      info: []
    };

    // Ensure conflicts is always an array
    const conflictArray = Array.isArray(conflicts) ? conflicts : [];
    conflictArray.forEach((conflict, index) => {
      const severity = conflict.severity || 'info';
      grouped[severity].push({ ...conflict, originalIndex: index });
    });

    return grouped;
  };

  const groupedConflicts = groupConflictsBySeverity();
  const totalConflicts = Array.isArray(conflicts) ? conflicts.length : 0;
  const autoFixableConflicts = Array.isArray(conflicts) ? conflicts.filter(c => 
    ['duplicate_player', 'unmatched_partner_request', 'pace_overflow', 'pace_violation'].includes(c.type)
  ) : [];

  return (
    <div className="conflict-panel">
      <div className="panel-header">
        <h3>Conflicts</h3>
        <div className="header-controls">
          <Badge
            variant={totalConflicts > 0 ? 'destructive' : 'success'}
            className="conflict-count-badge"
          >
            {totalConflicts}
          </Badge>
          {autoFixableConflicts.length > 0 && onAutoFixConflicts && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAutoFixConflicts(autoFixableConflicts)}
              title={`Auto-fix ${autoFixableConflicts.length} fixable conflicts`}
            >
              <Wrench className="h-3 w-3 mr-1" />
              Fix All
            </Button>
          )}
          {onRefreshConflicts && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRefreshConflicts}
              disabled={isRefreshing}
              title="Refresh conflict detection"
            >
              <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
            </Button>
          )}
        </div>
      </div>

      {totalConflicts === 0 ? (
        <div className="no-conflicts-state">
          <Badge variant="success" className="success-badge">
            <Check className="h-4 w-4 mr-1" />
            No conflicts detected
          </Badge>
          <p className="text-sm text-muted-foreground mt-2">All players are properly assigned</p>
        </div>
      ) : (
        <div className="conflicts-container">
          {Object.entries(groupedConflicts).map(([severity, severityConflicts]) => (
            severityConflicts.length > 0 && (
              <div key={severity} className={`severity-section ${severity}`}>
                <div className="severity-header">
                  <Badge variant={getSeverityVariant(severity)} className="severity-badge">
                    {getSeverityIcon(severity)} {severity.charAt(0).toUpperCase() + severity.slice(1)}
                    <span className="ml-1">({severityConflicts.length})</span>
                  </Badge>
                </div>

                <Accordion type="multiple" className="conflict-accordion">
                  {severityConflicts.map((conflict) => (
                    <AccordionItem
                      key={conflict.originalIndex}
                      value={`conflict-${conflict.originalIndex}`}
                      className={cn("conflict-item", severity)}
                    >
                      <AccordionTrigger className="conflict-trigger">
                        <span className="conflict-type">
                          {formatConflictType(conflict.type)}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="conflict-content">
                        <p className="conflict-reason text-sm text-muted-foreground">{conflict.reason}</p>
                        {renderConflictDetails(conflict, conflict.originalIndex)}

                        <div className="conflict-actions">
                          {['duplicate_player', 'unmatched_partner_request', 'pace_overflow', 'pace_violation'].includes(conflict.type) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onAutoFixConflicts([conflict])}
                              title="Automatically fix this conflict"
                            >
                              <Wrench className="h-3 w-3 mr-1" />
                              Auto-Fix
                            </Button>
                          )}
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => onConflictResolve(conflict.originalIndex)}
                            title="Mark as resolved"
                          >
                            <Check className="h-3 w-3 mr-1" />
                            Mark Resolved
                          </Button>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
}

export default ConflictPanel;